import type { SkinSeoulAdminProduct } from '../../../../models/ProductTypes';

/** One entry of `PATCH /products/admin` `variations[].images`. The list replaces the variation's images. */
export interface VariationImagePayloadItem {
  src: string;
  alt?: string;
}

const isSameList = (a: string[], b: string[]) => a.length === b.length && a.every((url, index) => url === b[index]);

/**
 * The images to show for a variation. An inherited variation shows the parent's current list, so a
 * parent image change made in this session is reflected without a refetch.
 */
export const getVariationDisplayImages = (variation: SkinSeoulAdminProduct, parentImageUrls?: string[]): string[] =>
  variation.hasOwnImages === false && parentImageUrls ? parentImageUrls : variation.productImageUrls || [];

/** Drops the variation's own images so it shows - and, once saved, inherits - the parent's again. */
export const resetVariationImagesToParent = (
  variation: SkinSeoulAdminProduct,
  parentImageUrls: string[] = [],
): SkinSeoulAdminProduct => ({ ...variation, productImageUrls: [...parentImageUrls], hasOwnImages: false });

/**
 * Applies a user edit. The edited list is the variation's own from then on; an empty list has
 * nothing of its own left, which is the same as resetting to the parent.
 */
export const withEditedVariationImages = (
  variation: SkinSeoulAdminProduct,
  imageUrls: string[],
  parentImageUrls: string[] = [],
): SkinSeoulAdminProduct =>
  imageUrls.length === 0
    ? resetVariationImagesToParent(variation, parentImageUrls)
    : { ...variation, productImageUrls: [...imageUrls], hasOwnImages: true };

/**
 * The `images` field for a variation's save payload, or nothing when the images were not changed.
 *
 * An untouched variation must not send `images`: an inherited variation's URLs are the parent's,
 * and sending them would copy them onto the variation as its own. `images: []` clears the
 * variation's own images so it inherits the parent's again.
 */
export const buildVariationImagesPayload = (
  current: SkinSeoulAdminProduct,
  original: SkinSeoulAdminProduct | null,
): { images?: VariationImagePayloadItem[] } => {
  if (!original) {
    return {};
  }

  if (current.hasOwnImages === false) {
    // Reset in this session. Nothing to clear when it already inherited.
    return original.hasOwnImages === false ? {} : { images: [] };
  }

  const currentUrls = current.productImageUrls || [];
  if (isSameList(currentUrls, original.productImageUrls || [])) {
    return {};
  }

  return { images: currentUrls.map((src) => ({ src })) };
};

const describeImages = (product: SkinSeoulAdminProduct, imageCount: number) => {
  if (product.hasOwnImages === false) {
    return 'Inherited from parent';
  }
  return `${imageCount} own image${imageCount === 1 ? '' : 's'}`;
};

/** A human-readable before/after for the save confirmation, or null when no images will be sent. */
export const describeVariationImageChange = (
  current: SkinSeoulAdminProduct,
  original: SkinSeoulAdminProduct | null,
): { old: string; new: string } | null => {
  const { images } = buildVariationImagesPayload(current, original);
  if (!images || !original) {
    return null;
  }

  return {
    old: describeImages(original, original.productImageUrls?.length || 0),
    new: images.length === 0 ? 'Inherited from parent' : describeImages(current, images.length),
  };
};
